import React, { useState } from 'react';
import Navbar from '../../components/navbar/Navbar';
import Footer from '../../components/footer/Footer';
import { SIGNUP_URL } from '../../config/links';
import { formatDate, formatTime, monthDays, shiftMonth, vancouverToday } from './scheduleData';
import useSchedule from './useSchedule';
import './schedule.css';

function Session({ item }) {
  return <article className={`schedule-session${item.cancelled ? ' is-cancelled' : ''}`}>
    {item.cancelled && <strong className="schedule-cancelled">Cancelled</strong>}
    <h4>{item.classType}</h4>
    <p className="schedule-session-time">{formatTime(item.startMinutes)} – {formatTime(item.endMinutes)}{item.overnight && ' (next day)'}</p>
    <p>{item.cancellationReason || item.location}</p>
  </article>;
}

export default function Schedule() {
  const today = vancouverToday();
  const [month, setMonth] = useState(() => today.slice(0, 7));
  const [selectedDate, setSelectedDate] = useState(today);
  const { data, loading, error, refresh } = useSchedule();
  const sessionsByDate = {};
  data?.sessions.forEach(item => { (sessionsByDate[item.date] ||= []).push(item); });
  const selectedSessions = sessionsByDate[selectedDate] || [];
  const changeMonth = delta => {
    const next = shiftMonth(month, delta);
    setMonth(next);
    setSelectedDate(next === today.slice(0, 7) ? today : `${next}-01`);
  };

  return <><Navbar /><main className="schedule-main">
    <h1 className="schedule-title">CLASS SCHEDULE</h1>
    <p className="schedule-intro">Everyone is welcome. For current membership pricing, drop-ins, and free trial registration, <a href={SIGNUP_URL} target="_blank" rel="noopener noreferrer">visit our Linktree</a>.</p>
    {data && <h2 className="schedule-term">{data.tab.name}</h2>}
    <nav className="schedule-month-controls" aria-label="Schedule month">
      <button type="button" onClick={() => changeMonth(-1)}>← Previous Month</button>
      <button type="button" onClick={() => { setMonth(today.slice(0, 7)); setSelectedDate(today); }}>This Month</button>
      <button type="button" onClick={() => changeMonth(1)}>Next Month →</button>
    </nav>
    <h2 className="schedule-month-heading" aria-live="polite">{formatDate(`${month}-01`, { month: 'long', year: 'numeric', day: undefined })}</h2>
    {loading && !data && <p role="status">Loading schedule…</p>}
    {error && <div className="schedule-notice" role="alert"><p>{data ? 'Unable to update; showing saved schedule.' : 'The schedule is temporarily unavailable.'}</p><button type="button" disabled={loading} onClick={refresh}>Try again</button></div>}
    {!!data?.skippedRows && <p className="schedule-notice" role="status">Some sessions could not be read and may be missing.</p>}
    {data && <>
      <section className="schedule-calendar" aria-label={`${formatDate(`${month}-01`, { month: 'long', year: 'numeric', day: undefined })} calendar`}>
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => <div className="calendar-weekday" key={day} aria-hidden="true">{day}</div>)}
        {monthDays(month).map(date => {
          if (!date.startsWith(month)) return <div key={date} className="calendar-outside" aria-hidden="true" />;
          const sessions = sessionsByDate[date] || [];
          const description = sessions.map(item => `${item.cancelled ? 'Cancelled: ' : ''}${item.classType}, ${formatTime(item.startMinutes)} to ${formatTime(item.endMinutes)}, ${item.cancellationReason || item.location}`).join('; ');
          return <button type="button" key={date} className={`calendar-day${date === today ? ' is-today' : ''}`} aria-pressed={date === selectedDate} aria-label={`${formatDate(date, { weekday: 'long', year: 'numeric' })}. ${description || 'No sessions'}`} onClick={() => setSelectedDate(date)}>
            <time dateTime={date} className="calendar-number">{Number(date.slice(-2))}</time>
            <span className="calendar-events">{sessions.map(item => <span className={`calendar-event${item.cancelled ? ' is-cancelled' : ''}`} key={item.id}>
              {item.cancelled && <strong>Cancelled</strong>}<span>{item.classType}</span><span>{formatTime(item.startMinutes)} – {formatTime(item.endMinutes)}{item.overnight && ' (+1 day)'}</span><span>{item.cancellationReason || item.location}</span>
            </span>)}</span>
            <span className="calendar-markers" aria-hidden="true">{sessions.map(item => <span key={item.id} className={item.cancelled ? 'cancelled-dot' : 'class-dot'} />)}</span>
          </button>;
        })}
      </section>
      <div className="calendar-mobile-legend" aria-hidden="true"><span><i className="class-dot" /> Class</span><span><i className="cancelled-dot" /> Cancelled</span></div>
      <section className="calendar-day-details" aria-label="Selected day sessions" aria-live="polite">
        <h3>{formatDate(selectedDate, { weekday: 'long', year: 'numeric' })}</h3>
        {selectedSessions.length ? selectedSessions.map(item => <Session key={item.id} item={item} />) : <p>No sessions on this day.</p>}
      </section>
      {!data.sessions.some(item => item.date.startsWith(month)) && <p className="schedule-empty">No sessions are listed for this month.</p>}
    </>}
  </main><Footer /></>;
}
